import { useCallback, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getEditModeStepIds, SEGMENT_BUILDER_TOUR, TOUR_STEP_ID } from "./segmentBuilderTour";
import { createSegmentStepContent } from "./segmentStepContent";

import {
  SEGMENT_MODE,
  SEGMENT_TAB,
  type SegmentMode,
  type SegmentTab,
} from "@/features/segments/builder/constants";
import { useSegmentPaths } from "@/features/segments/builder/hooks/useSegmentPaths";
import type { SegmentFormValues } from "@/features/segments/builder/schemas";
import { getSegmentTab, withSegmentTab } from "@/features/segments/builder/segmentTabParams";
import type { SegmentField } from "@/features/segments/types";
import { buildTourSteps } from "@/features/tours/buildTourSteps";
import { TourRunner } from "@/features/tours/components/TourRunner";
import { TOUR_PARAM, TOUR_STEP_PARAM } from "@/features/tours/constants";
import { getCanonicalStep } from "@/features/tours/tourSteps";
import { useTour } from "@/features/tours/useTour";
import { useTourTargets } from "@/features/tours/useTourTarget";

const TOUR_STARTED_IN_VIEW_PARAM = "tourStartedInView";

const STEP_TAB: Partial<Record<string, SegmentTab>> = {
  [TOUR_STEP_ID.VACANCIES]: SEGMENT_TAB.VACANCIES,
  [TOUR_STEP_ID.VACANCY_SEARCH]: SEGMENT_TAB.VACANCIES,
  [TOUR_STEP_ID.VACANCY_FILTERS]: SEGMENT_TAB.VACANCIES,
  [TOUR_STEP_ID.VACANCY_LISTS]: SEGMENT_TAB.VACANCIES,
  [TOUR_STEP_ID.VACANCY_FLIP]: SEGMENT_TAB.VACANCIES,
  [TOUR_STEP_ID.HISTORY_TIMELINE]: SEGMENT_TAB.HISTORY,
};

type SegmentTourHostProps = {
  mode: SegmentMode;
  segmentId?: string;
  readOnly: boolean;
  segmentCount?: number;
  fields: SegmentField[];
  onStepChange: (stepId: string) => void;
};

function RunningSegmentTour({
  mode,
  segmentId,
  readOnly,
  segmentCount,
  fields,
  onStepChange,
}: SegmentTourHostProps) {
  const { t } = useTranslation();
  const { stepIds, stop } = useTour();
  const targets = useTourTargets();
  const firstRule = useWatch<SegmentFormValues, "rules.0">({ name: "rules.0" });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const paths = useSegmentPaths();
  const editPath = segmentId ? paths.edit(segmentId) : undefined;
  const activeTab = getSegmentTab(searchParams);
  const editModeStepIds = getEditModeStepIds({ hasRuleToShow: firstRule !== undefined });
  const startedInView = searchParams.has(TOUR_STARTED_IN_VIEW_PARAM);

  const prepareStep = useCallback(
    (stepId: string | undefined) => {
      const stepTab = (stepId && STEP_TAB[stepId]) ?? SEGMENT_TAB.RULES;
      if (stepId && editPath && mode === SEGMENT_MODE.VIEW && editModeStepIds.has(stepId)) {
        const next = withSegmentTab(searchParams, stepTab);
        next.set(TOUR_PARAM, SEGMENT_BUILDER_TOUR.id);
        next.set(TOUR_STEP_PARAM, String(getCanonicalStep(SEGMENT_BUILDER_TOUR, stepId)));
        next.set(TOUR_STARTED_IN_VIEW_PARAM, "true");
        // View and edit share one TourProvider, so a running tour would remount with its old
        // starting step; stop it and let the edit page's deep link start it at this step.
        stop();
        navigate(`${editPath}?${next}`, { replace: true });
        return false;
      }
      if (stepTab !== activeTab) {
        setSearchParams((prev) => withSegmentTab(prev, stepTab), { replace: true });
        return false;
      }
      if (stepId) onStepChange(stepId);
      return true;
    },
    [editPath, mode, editModeStepIds, searchParams, stop, navigate, activeTab, setSearchParams, onStepChange],
  );

  const handleEnd = useCallback(() => {
    if (!segmentId || !startedInView || mode !== SEGMENT_MODE.EDIT) return;
    const next = new URLSearchParams(searchParams);
    next.delete(TOUR_STARTED_IN_VIEW_PARAM);
    navigate({ pathname: paths.view(segmentId), search: next.toString() }, { replace: true });
  }, [segmentId, startedInView, mode, searchParams, navigate, paths]);

  const steps = useMemo(
    () =>
      buildTourSteps({
        definition: SEGMENT_BUILDER_TOUR,
        stepIds,
        t,
        getTarget: targets.get,
        getContent: createSegmentStepContent({
          stepIds,
          t,
          readOnly,
          segmentCount,
          firstRule,
          fields,
        }),
      }),
    [stepIds, t, targets.get, readOnly, segmentCount, firstRule, fields],
  );

  return <TourRunner steps={steps} prepareStep={prepareStep} onEnd={handleEnd} />;
}

export function SegmentTourHost(props: SegmentTourHostProps) {
  const { isRunning } = useTour();
  if (!isRunning) return null;
  return <RunningSegmentTour {...props} />;
}
